import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import { summarizeFilter } from '../lib/filterSummary';
import { formatBarRange } from '../lib/timelineMath';
import type { ConflictEdge, DesignConfig } from '../api/types';

interface Props {
  config: DesignConfig;
  storeId: string;
  conflicts?: ConflictEdge[];
  onClose: () => void;
  onSaved: () => void;
}

function toDateLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDateLocal(s: string): string | null {
  if (!s) return null;
  return new Date(s).toISOString();
}

export function ConfigBarPopover({ config, storeId, conflicts = [], onClose, onSaved }: Props) {
  const [from, setFrom] = useState(toDateLocal(config.active_from));
  const [to, setTo] = useState(toDateLocal(config.active_to));
  const [saveError, setSaveError] = useState<string | null>(null);

  const saveM = useMutation({
    mutationFn: () =>
      api.putConfig(
        {
          ...config,
          active_from: fromDateLocal(from),
          active_to: fromDateLocal(to),
        },
        storeId,
      ),
    onSuccess: () => onSaved(),
    // W-C [UX-7]: a failed window save closed silently — the operator believed
    // the schedule changed.
    onError: (e: unknown) =>
      setSaveError(e instanceof HttpError ? e.detail : (e as Error).message),
  });

  const winsOver = conflicts.filter((c) => c.winner === config.name);
  const hiddenBy = conflicts.filter((c) => c.loser === config.name);

  const summary = summarizeFilter(config.filter);

  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ff-forest/30 backdrop-blur-sm animate-rise px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="config-bar-popover-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ff-card w-full max-w-md p-6 space-y-5 my-auto">
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-widish text-ff-mute">
              Config · priority {config.priority}
            </p>
            <h3
              id="config-bar-popover-title"
              className="font-display text-2xl text-ff-forest truncate"
            >
              {config.name}
            </h3>
            <p
              className="mt-1.5 text-[12px] text-ff-mute font-mono leading-snug break-words"
              data-testid="popover-filter-summary"
              title={summary}
            >
              <span className="text-ff-mute/60 mr-1">filter:</span>
              <span className="text-ff-ink/80">{summary}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 -mr-2 -mt-2 grid place-items-center rounded-full text-ff-mute hover:text-ff-forest hover:bg-ff-mint/50 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <path
                d="M3 3 L11 11 M11 3 L3 11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="ff-divider-rule" />

        <div className="space-y-4">
          <div>
            <p className="ff-label">Currently</p>
            <p className="font-mono text-[12px] text-ff-forest">
              {formatBarRange(config.active_from, config.active_to)}
            </p>
          </div>

          <div>
            <label className="ff-label" htmlFor="popover-from">
              Active from
            </label>
            <div className="flex gap-2">
              <input
                id="popover-from"
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="ff-input flex-1 font-mono text-sm px-3"
                data-testid="popover-active-from"
              />
              <button
                type="button"
                onClick={() => setFrom('')}
                disabled={!from}
                className="font-mono text-[10px] uppercase tracking-widish text-ff-mute hover:text-ff-forest disabled:opacity-30 disabled:cursor-not-allowed px-2"
              >
                Clear
              </button>
            </div>
          </div>

          <div>
            <label className="ff-label" htmlFor="popover-to">
              Active until
            </label>
            <div className="flex gap-2">
              <input
                id="popover-to"
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="ff-input flex-1 font-mono text-sm px-3"
                data-testid="popover-active-to"
              />
              <button
                type="button"
                onClick={() => setTo('')}
                disabled={!to}
                className="font-mono text-[10px] uppercase tracking-widish text-ff-mute hover:text-ff-forest disabled:opacity-30 disabled:cursor-not-allowed px-2"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {(winsOver.length > 0 || hiddenBy.length > 0) && (
          <div className="rounded-xl border border-ff-warning/30 bg-ff-warning-soft px-3 py-2.5 space-y-1.5">
            <p className="font-mono text-[10px] uppercase tracking-widish text-ff-warning">
              Overlaps
            </p>
            {winsOver.length > 0 && (
              <p className="text-[12px] text-ff-ink/80 leading-snug">
                Wins over{' '}
                {winsOver.map((c, i) => (
                  <span key={c.loser}>
                    <span className="font-mono text-ff-forest">{c.loser}</span>
                    {i < winsOver.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </p>
            )}
            {hiddenBy.length > 0 && (
              <p className="text-[12px] text-ff-ink/80 leading-snug">
                Hidden by{' '}
                {hiddenBy.map((c, i) => (
                  <span key={c.winner}>
                    <span className="font-mono text-ff-forest">{c.winner}</span>
                    {i < hiddenBy.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </p>
            )}
          </div>
        )}

        {saveError && (
          <p className="text-sm text-ff-danger" role="alert" data-testid="popover-save-error">
            Couldn't save the window: {saveError}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="ff-btn-ghost">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveM.mutate()}
            disabled={saveM.isPending}
            className="ff-btn-primary"
            data-testid="popover-save"
          >
            {saveM.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
